import { computed, ComputedRef, Ref, unref, ref, MaybeRef } from 'vue'
import { ApolloClient } from '@apollo/client/core'
import { TypedDocumentNode } from '@graphql-typed-document-node/core'
import {
  OrganisationFragmentDef,
  CompanyAccountsQuery,
  CompanyFragmentDef,
  CompanyOrganisationsFragmentDef,
  FieldTypeFragmentDef,
  FullTypeFragmentDef,
  InputValueFragmentDef,
  IntrospectionQuery,
  TypeRefFragmentDef,
} from '@/components/GraphqlDataTable/queries'
import { useFragment } from '@/gql'
import {
  FullTypeFragment,
  CompanyAccountFragment,
  InputValueFragment,
  FieldTypeFragment,
  OrganisationAccountFragment,
} from '@/gql/graphql'
import { ListItem } from '@/components/Workflows/Pages/Workflow/workflow'
import { provider } from '@/app-bootstrapping/SetUpApollo'
import LookupFilters from '@/components/GraphqlDataTable/filters'
import { FilterTypes } from '@/components/GraphqlDataTable/filters/filter-enums'
import { convertArgTypeToTypeStr, getFieldTypeLeaf } from '@/components/GraphqlDataTable/tools'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { FilterMetaDataProp } from '@/components/GraphqlDataTable/filters/types'

const WarningKey = '[GRAPHQL-DATATABLE-WARN]'

function customProvide<TResult = any>(
  client: ApolloClient<any>,
  query: TypedDocumentNode<TResult, any>,
) {
  const loading = ref(true)
  const result: Ref<TResult | undefined> = ref()
  client
    .query<TResult>({ query })
    .then((data) => (result.value = data.data))
    .finally(() => (loading.value = false))

  return {
    loading,
    result,
  }
}

/**
 * @param {string} clientName The name of the apollo client we want to grab introspection information on
 */
export const useIntrospectionTypes = (
  clientName: string = 'publicApiCustomFields',
): {
  introspection: any
  typeLookup: ComputedRef<Record<string, FullTypeFragment>>
  queryType: ComputedRef<FullTypeFragment | null>
  loading: Ref<boolean>
} => {
  const client = provider.clients[clientName]
  if (!client) throw new Error(`Client not found, was given: ${clientName}`)

  const { loading, result: introspection } = customProvide(client, IntrospectionQuery)

  const typeLookup: ComputedRef<Record<string, FullTypeFragment>> = computed(() => {
    if (loading.value || !introspection.value?.__schema) return {} // Return empty object if data is still loading

    // Destructure types from the introspection result
    const {
      __schema: { types },
    } = introspection.value

    // Reduce the types array into an object with type names as keys
    return (
      types
        .map((type) => useFragment(FullTypeFragmentDef, type))
        .filter(({ name }: FullTypeFragment) => name !== null && name !== undefined)
        // Since we just filtered out nulls and undefineds its safe to do a forced cast here
        .reduce((acc, type) => ({ ...acc, [type.name as string]: type }), {})
    )
  })

  // Contains information on the Query type of the client. Basically contains information on all our endpoints
  const queryType: ComputedRef<FullTypeFragment | null> = computed(() => {
    if (loading.value || !introspection) return null // Return null if we don't have data yet

    // Destructure queries from the introspection result
    return useFragment(FullTypeFragmentDef, introspection.value?.__schema.queryType) ?? null
  })

  return { typeLookup, queryType, introspection, loading }
}

/**
 * Fetches the authenticated users accounts and organisations accounts and returns them all in an array
 * @param clientName The name of the apollo client we want to grab introspection information on
 * @param useSubdivisions If we should include the organisations subdivisions for companies parent organisations
 */
export const useCompanyAccounts = ({
  clientName = 'publicApiCustomFields',
  useSubdivisions = false,
} = {}): {
  accounts: ComputedRef<(ListItem & (CompanyAccountFragment | OrganisationAccountFragment))[]>
  loading: Ref<boolean>
} => {
  const client = provider.clients[clientName]
  if (!client) throw new Error(`Client not found, was given: ${clientName}`)
  const { result, loading } = customProvide(client, CompanyAccountsQuery)

  // Computed property to process and flatten the accounts data
  const accounts: ComputedRef<
    (ListItem & (CompanyAccountFragment | OrganisationAccountFragment))[]
  > = computed(() => {
    const seen = {} // Object to keep track of processed account IDs to avoid duplicates

    const flatOrganisations = (org: OrganisationAccountFragment) => [
      org,
      ...org.divisions.data.map((c) => useFragment(CompanyFragmentDef, c)),
    ]

    return (
      result.value?.accounts
        .flatMap((acc) => {
          // If the account is an 'Organisation', include its divisions
          if (acc.__typename === 'Organisation') {
            const org = useFragment(OrganisationFragmentDef, acc)
            // Return an array containing the organisation and its divisions
            return flatOrganisations(org)
          }
          // Otherwise, return the account as is
          if (acc.__typename === 'Company') {
            return [
              useFragment(CompanyFragmentDef, acc),
              ...(useSubdivisions
                ? useFragment(CompanyOrganisationsFragmentDef, acc)
                    .organisations.data.map((org) => useFragment(OrganisationFragmentDef, org))
                    .flatMap(flatOrganisations)
                : []),
            ]
          }
          return []
        })
        .filter((acc) =>
          // Filter out duplicate accounts based on their ID
          acc !== undefined && seen.hasOwnProperty(acc.id) ? false : (seen[acc.id] = true),
        )
        .map((acc: OrganisationAccountFragment | CompanyAccountFragment) => ({
          // Map each account to an object with 'value', 'label', and other account properties
          value: acc.id,
          label: acc.name,
          ...acc,
        })) ?? []
    )
  })

  return { accounts, loading }
}

const ARG_BREAK_TOKEN = '<ARG_BREAK>'
// eslint-disable-next-line
// @ts-expect-error ts hates the named capturing group
const queryArgsReg = /query[^(]+[(](?<args>[^)]+)/
// eslint-disable-next-line
// @ts-expect-error ts hates the named capturing groups
const queryTypeReg = /query.+\{[ \n]+(?<query>[^(]+)\((?<args>[^)]+)/

// Function takes and cleans + normalizes the argument strings, like $foo: Bar, $bar: Foo\n $Cee: $Doo\n
// Will instead become $foo: Bar<ARG_BREAK> $bar: Foo<ARG_BREAK> $Cee: $Doo
// Making it easier to split the arguments cleanly
const argumentCleaning = (argStrs: string | undefined) =>
  argStrs?.trim()?.replaceAll(',', ARG_BREAK_TOKEN)?.replaceAll('\n', ARG_BREAK_TOKEN)

// Returns the main type the query is reciving. Ie are we querying hires, then it returns the Hire type information
// It also returns the arguments that are part of the query. Including the inputted args and all args that are part of
// the query itself.
export const useQueryType = (
  query: string | Ref<string>,
  clientName: string = 'publicApiCustomFields',
): {
  args: ComputedRef<{
    inputtedArgs: InputValueFragment[]
    allArgs: InputValueFragment[]
    argNameToInputtedArgName: Record<string, string>
  }>
  type: ComputedRef<null | FieldTypeFragment>
} => {
  const qRegs = computed(() => {
    const q = unref(query)
    const argsStr = q.match(queryArgsReg)?.groups?.args
    const { query: queryTypeStr, args: queryArgsDefStr } = q.match(queryTypeReg)?.groups ?? {}

    const args =
      argumentCleaning(argsStr)
        ?.split(ARG_BREAK_TOKEN)
        ?.map((arg) => arg.trim().split(':'))
        ?.map(([name, typeStr]) => ({ name, typeStr })) ?? []

    const queryArgsMap: Record<string, string> =
      argumentCleaning(queryArgsDefStr)
        ?.split(ARG_BREAK_TOKEN)
        .map((arg) => arg.trim().split(':'))
        .reduce((acc, [argName, inputArg]) => ({ ...acc, [inputArg.trim()]: argName }), {}) ?? {}

    return { args, queryTypeStr, queryArgsDefStr, queryArgsMap }
  })

  const { queryType } = useIntrospectionTypes(clientName)

  const type: ComputedRef<null | FieldTypeFragment> = computed(() => {
    const typeSearched = queryType.value?.fields
      ?.map((f) => useFragment(FieldTypeFragmentDef, f))
      .find(({ name }) => qRegs.value.queryTypeStr === name)
    if (!typeSearched) return null
    return typeSearched
  })

  const args: ComputedRef<{
    inputtedArgs: InputValueFragment[]
    allArgs: InputValueFragment[]
    argNameToInputtedArgName: Record<string, string>
  }> = computed(() => {
    const { args: argNames, queryArgsMap } = qRegs.value

    const allArgs = type.value?.args.map((a) => useFragment(InputValueFragmentDef, a)) ?? []
    // These are the arguments that have been inputted in the query.
    const inputtedArgs: InputValueFragment[] = argNames
      .map(({ name: argName }) => allArgs.find(({ name }) => queryArgsMap?.[argName] === name))
      .filter((v): v is InputValueFragment => v !== undefined)

    // Maps the argument names used in the query to the argument names that are inputted
    // query MyQuery($myInput: string) {
    //     myQuery(myOtherInput: $myInput) {
    //        ....
    //     }
    // }
    // Would result in a mapping like { myOtherInput: myInput }
    const argNameToInputtedArgName: Record<string, string> = Object.keys(queryArgsMap).reduce(
      (acc, name) => ({ ...acc, [queryArgsMap[name]]: name.replace('$', '') }),
      {},
    )
    return { inputtedArgs, allArgs, argNameToInputtedArgName }
  })

  return { type, args }
}

export const useQueryArgs = (
  query: MaybeRef<string>,
  generateAllArgs: MaybeRef<boolean> = false,
  filterBlackList: MaybeRef<string[]> = [],
) => {
  const { type: queryType, args: queryArgs } = useQueryType(query)
  const { typeLookup, loading } = useIntrospectionTypes()

  const enumTypeToSelector = (
    { leaf, arg, listType: multiSelect },
    inputArgName: string,
    filterKeys: string[],
  ) => {
    const { enumValues, description } = typeLookup.value[leaf.name]
    return {
      list:
        enumValues?.map((value) => ({ ...value, label: value.description, value: value.name })) ??
        [],
      title: $t(queryArgs.value.argNameToInputtedArgName[arg.name]),
      inputArgName,
      name: arg.name,
      filterKeys,
      description: arg.description,
      placeholder: description,
      multiSelect,
      type: FilterTypes.SELECT,
    }
  }

  const scalarTypeToSelector = (
    { leaf, arg, listType },
    inputArgName: string,
    filterKeys: string[],
  ) => {
    switch (leaf.name) {
      case 'Boolean':
        return {
          type: FilterTypes.TOGGLE,
          inputArgName,
          filterKeys,
          name: arg.name,
          title: arg.name,
          description: arg.description,
        }
      case 'String':
        if (listType) {
          console.warn(
            WarningKey,
            `We don't support generating multi select for dynamic strings as the conditions are too vague.\nBuild a dedicated filter to support the graphql input ${inputArgName} with one of the following keys`,
            filterKeys,
          )
          return null
        }
        // Do stuff here
        return {
          type: FilterTypes.SINGLE_STRING,
          inputArgName,
          filterKeys,
          name: arg.name,
          title: arg.name,
          description: arg.description,
        }
      default:
        console.warn(
          WarningKey,
          `We don't support generating a scalar type filter for the type ${arg.name}.\nBuild a dedicated filter to support the graphql input for this type with one of the following keys`,
          filterKeys,
        )
        return null
    }
  }

  const containsListType = (field) => {
    let { type } = field
    // Unwrap the type until there is no more 'ofType'
    do {
      const { ofType, kind } = type
      if (kind === 'LIST') return true
      type = ofType
    } while (type)

    return false
  }

  const blackList = computed(() => ['first', 'page', 'orderBy', ...unref(filterBlackList)])

  const argToSpecificKey = (arg) => `${arg.name}-${convertArgTypeToTypeStr(arg.type)}`
  const argToGenericKey = (arg) => `${convertArgTypeToTypeStr(arg.type)}`

  const dynamicArgs: ComputedRef<FilterMetaDataProp[]> = computed(() => {
    const selected = queryType.value
    if (!selected) return []

    const hardCodedArgs = blackList.value
    const inputtedArgNames = queryArgs.value.inputtedArgs.map(({ name }) => name)
    const argMapping = queryArgs.value.argNameToInputtedArgName
    const ENUM_TYPE = 'ENUM'
    const SCALAR_TYPE = 'SCALAR'
    // Build the arguments string for the query
    const args = selected.args
      .map((arg) => useFragment(InputValueFragmentDef, arg))
      .filter((arg) => {
        const argName = arg.name
        return (
          !hardCodedArgs.includes(argName) &&
          (unref(generateAllArgs) || inputtedArgNames.includes(argName))
        )
      })
      .map((arg) => ({
        arg,
        leaf: getFieldTypeLeaf(useFragment(TypeRefFragmentDef, arg.type)),
        listType: containsListType(arg),
      }))

    // We sort the args to align as best we can with the inputtedArgs to allow control over the ordering
    args.sort(
      ({ arg: argA }, { arg: argB }) =>
        inputtedArgNames.findIndex((name) => argA.name === name) -
        inputtedArgNames.findIndex((name) => argB.name === name),
    )

    return (
      args
        .map((arg) => {
          // This mapping function will try to find a component that will map an argument to a function in the following order
          // Given the query
          // query SomeQuery($arg1: Type) {
          //    someQuery(arg2: $arg1) {
          //
          //    }
          // }
          // First it will check for an arg with the key arg1-Type
          // then it will check for arg2-Type
          // and lastly for Type
          const argKeyCustomSpecific = `${argMapping[arg.arg.name]}-${convertArgTypeToTypeStr(arg.arg.type)}`
          const argKeySpecific = argToSpecificKey(arg.arg)
          const argKeyGeneric = argToGenericKey(arg.arg)
          const filterKeys = [argKeyCustomSpecific, argKeySpecific, argKeyGeneric]
          if (
            LookupFilters[argKeyCustomSpecific] &&
            LookupFilters[argKeyCustomSpecific].component
          ) {
            return {
              arg,
              dynamicArg: {
                type: FilterTypes.COMPONENT,
                component: LookupFilters[argKeyCustomSpecific].component,
                inputArgName: argMapping[arg.arg.name],
                filterKeys,
                name: argMapping[arg.arg.name],
                description: arg.arg.description,
              },
            }
          }
          if (LookupFilters[argKeySpecific] && LookupFilters[argKeySpecific].component) {
            return {
              arg,
              dynamicArg: {
                type: FilterTypes.COMPONENT,
                component: LookupFilters[argKeySpecific].component,
                filterKeys,
                inputArgName: argMapping[arg.arg.name],
                name: arg.arg.name,
                description: arg.arg.description,
              },
            }
          }
          if (LookupFilters[argKeyGeneric] && LookupFilters[argKeyGeneric].component) {
            return {
              arg,
              dynamicArg: {
                type: FilterTypes.COMPONENT,
                component: LookupFilters[argKeyGeneric].component,
                filterKeys,
                inputArgName: argMapping[arg.arg.name],
                name: arg.arg.name,
                description: arg.arg.description,
              },
            }
          }

          switch (arg.leaf.kind) {
            case SCALAR_TYPE:
              return {
                arg,
                dynamicArg: scalarTypeToSelector(arg, argMapping[arg.arg.name], filterKeys),
              }
            case ENUM_TYPE:
              return {
                arg,
                dynamicArg: enumTypeToSelector(arg, argMapping[arg.arg.name], filterKeys),
              }
          }
          console.warn(
            WarningKey,
            `Failed to solve filter for ${arg.arg.name}. Implement a custom filer with the one of the keys`,
            filterKeys,
            `to enable this filter`,
          )
          return { arg, dynamicArg: null }
        })
        .filter(({ dynamicArg }) => !!dynamicArg)
        .map(({ arg, dynamicArg }) => ({
          filter: dynamicArg as NonNullable<typeof dynamicArg>,
          graphqlData: arg,
        }))
        // Here we enforce the type since we have filtered out the nullable options in our filter
        .map((filter) => filter as unknown as FilterMetaDataProp)
    )
  })

  return { args: dynamicArgs, loading }
}

export const useQueryArg = (query: MaybeRef<string>, arg: MaybeRef<string>) => {
  const { args, loading } = useQueryArgs(query)
  const argName = computed(() => unref(arg))
  const dynamicArg = computed(() =>
    args.value.find((argument) => argument.filter.inputArgName === argName.value),
  )

  return { arg: dynamicArg, loading }
}
